import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Space, Empty, Spin, message } from 'antd';
import { SearchOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { Item } from '../../../types/item';
import hqDisplayGroupService from '../../../services/hqDisplayGroupService';
import { itemService } from '../../../services/itemService';
import { useTranslation } from 'react-i18next';

interface ItemSelectorProps {
  selectedItems: Item[];
  onItemsChange: (items: Item[]) => void;
  title: string;
  currentListType: string;
  mainDivision?: string;
  categoryIds?: string[];
  visible?: boolean;
}

const ItemSelector: React.FC<ItemSelectorProps> = ({ selectedItems, onItemsChange, title, currentListType, mainDivision, categoryIds, visible }) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [availableItems, setAvailableItems] = useState<Item[]>([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);

  // 获取可选商品列表
  const fetchItems = async (search?: string) => {
    try {
      setSearchLoading(true);
      // 构造查询参数，包含mainDivision和categoryIds
      const params: any = { searchStr: search };
      
      // 如果有mainDivision，则添加到查询参数中
      if (currentListType === 'black') {
        if (mainDivision) {
          params.mainDivision = mainDivision;
        }
        
        // 如果有categoryIds，则添加到查询参数中
        if (categoryIds && categoryIds.length > 0) {
          params.subClassSet = categoryIds;
        }
      }
      
      const response = await itemService.listItems(params);
      
      if (response.success && response.data) {
        // 过滤掉已选择的商品
        const selectedIds = selectedItems.map(item => item.id);
        const filteredItems = response.data.filter(item => !selectedIds.includes(item.id));
        setAvailableItems(filteredItems);
      } else {
        message.error(response.errorMessage || t('hqDisplayGroup.itemManagement.itemSelector.loadItemsFailed'));
      }
    } catch (error) {
      console.error('Failed to fetch item list:', error);
      message.error(t('hqDisplayGroup.itemManagement.itemSelector.loadItemsFailed'));
    } finally {
      setSearchLoading(false);
    }
  };

  // 初始加载 和 visible变化时重新查询
  useEffect(() => {
    if (visible !== false) {  // visible为undefined或true时都重新查询
      // 清空搜索条件，重新查询
      setSearchText('');
      fetchItems();
    }
  }, [visible, mainDivision, categoryIds]);

  // 处理搜索
  const handleSearch = () => {
    fetchItems(searchText);
  };

  // 处理添加商品
  const handleAddItem = (item: Item) => {
    const newSelectedItems = [...selectedItems, item];
    onItemsChange(newSelectedItems);
    // 从可用商品列表中移除已添加的商品，避免重新搜索
    setAvailableItems(prevItems => prevItems.filter(availableItem => availableItem.id !== item.id));
  };

  // 处理删除商品
  const handleRemoveItem = async (itemId: number) => {
    const newSelectedItems = selectedItems.filter(item => item.id !== itemId);
    onItemsChange(newSelectedItems);
    
    // 获取被删除的商品信息
    const removedItem = selectedItems.find(item => item.id === itemId);
    if (removedItem) {
      // 检查被删除的商品是否符合当前搜索条件
      const matchesSearch = !searchText || 
        removedItem.code.toLowerCase().includes(searchText.toLowerCase()) || 
        removedItem.name.toLowerCase().includes(searchText.toLowerCase());
      
      if (matchesSearch) {
        // 将符合条件的商品重新添加到可用列表中
        setAvailableItems(prevItems => [...prevItems, removedItem]);
      }
    }
  };

  // 可选商品列表列定义
  const availableColumns = [
    {
      title: t('hqDisplayGroup.detail.itemCodeColumn'),
      dataIndex: 'code',
      key: 'code',
      fixed: 'left' as const,
      width: 80,
    },
    {
      title: t('hqDisplayGroup.detail.itemNameColumn'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
    },
    {
      title: t('hqDisplayGroup.table.actions'),
      key: 'action',
      width: 80,
      fixed: 'right' as const,
      render: (_: any, record: Item) => (
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          size="small"
          onClick={() => handleAddItem(record)}
        >
          {t('hqDisplayGroup.itemManagement.itemSelector.addItem')}
        </Button>
      ),
    },
  ];

  // 已选商品列表列定义
  const selectedColumns = [
    {
      title: t('hqDisplayGroup.detail.itemCodeColumn'),
      dataIndex: 'code',
      key: 'code',
      width: 120,
    },
    {
      title: t('hqDisplayGroup.detail.itemNameColumn'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
    },
    {
      title: t('hqDisplayGroup.table.actions'),
      key: 'action',
      width: 80,
      fixed: 'right' as const,
      render: (_: any, record: Item) => (
        <Button 
          danger 
          icon={<DeleteOutlined />} 
          size="small"
          onClick={() => handleRemoveItem(record.id)}
        >
          {t('hqDisplayGroup.itemManagement.itemSelector.removeItem')}
        </Button>
      ),
    },
  ];

  return (
    <div className="item-selector">
      <h3>{title}</h3>
      
      <div className="search-section">
        <Space>
          <Input
            placeholder={t('hqDisplayGroup.itemManagement.itemSelector.searchPlaceholder')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 250 }}
            allowClear
          />
          <Button 
            type="primary" 
            icon={<SearchOutlined />} 
            onClick={handleSearch}
            loading={searchLoading}
          >
            {t('common.search')}
          </Button>
        </Space>
      </div>

      <div className="tables-container" style={{ display: 'flex', flexDirection: 'column', marginTop: 16, gap: 24 }}>
        <div className="available-items">
          <h4>{t('hqDisplayGroup.itemManagement.itemSelector.availableItems')}</h4>
          <Table
            columns={availableColumns}
            dataSource={availableItems}
            rowKey="id"
            size="small"
            // pagination={{ pageSize: 5 }}
            loading={searchLoading}
            locale={{
              emptyText: <Empty description={t('hqDisplayGroup.itemManagement.itemSelector.noAvailableItems')} />
            }}
            scroll={{ x: 1200, y: 500 }}
          />
        </div>

        <div className="selected-items">
          <h4>{t('hqDisplayGroup.itemManagement.itemSelector.selectedItems', { count: selectedItems.length })}</h4>
          <Table
            columns={selectedColumns}
            dataSource={selectedItems}
            rowKey="id"
            size="small"
            // pagination={{ pageSize: 5 }}
            locale={{
              emptyText: <Empty description={t('hqDisplayGroup.itemManagement.itemSelector.noSelectedItems')} />
            }}
            scroll={{ x: 1200, y: 500 }}
          />
        </div>
      </div>
    </div>
  );
};

export default ItemSelector;